import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Seo from '../components/Seo';
import ShareGilda from '../components/ShareGilda';
import DeconstructedDish from '../components/DeconstructedDish';
import CanapesSection from '../components/CanapesSection';
import { GILDA_DECONSTRUCTED } from '../data/dishes';
import { GILDAS, QUIZ_QUESTIONS, SPICE_LABELS } from '../data/gildas';
import { breadcrumb, gildasItemList } from '../lib/seo-schemas';
import TakeawayCatering from '../components/TakeawayCatering';
import SmartImg from '../components/SmartImg';
import { waLink, stripAccents } from '../lib/contact';

function Spice({ level }) {
  if (level === null) {
    return (
      <div className="spice">
        <span style={{ fontSize: '0.65rem', letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--amber)', fontWeight: 500 }}>
          Temporada
        </span>
      </div>
    );
  }
  return (
    <div className="spice" aria-label={SPICE_LABELS[level]} title={SPICE_LABELS[level]}>
      {[0, 1, 2].map((i) => <span key={i} className={`sp ${i < level ? '' : 'o'}`} />)}
    </div>
  );
}

export default function Gildas() {
  const [step, setStep] = useState(0);
  const [votes, setVotes] = useState({});
  const quizRef = useRef(null);
  const { hash } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (hash === '#quiz' && quizRef.current) {
      setTimeout(() => quizRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 200);
    }
  }, [hash]);

  const answer = (gilda) => {
    setVotes(prev => ({ ...prev, [gilda]: (prev[gilda] || 0) + 1 }));
    setStep(s => s + 1);
  };
  const reset = () => { setStep(0); setVotes({}); };
  const result = Object.entries(votes).sort(([, a], [, b]) => b - a)[0]?.[0] || 'La Clásica';
  const resultGilda = GILDAS.find(g => g.name === result) || GILDAS[0];

  return (
    <>
      <Seo
        title="Las Gildas Artesanas"
        description="12 variedades de gilda artesana en Bar Cabrero (Torrelavega). Anchoa de Santoña DOP, piparra de Ibarra y aceitunas. Hechas a mano cada mañana. Para llevar y caterings. Haz el quiz: ¿cuál es tu gilda?"
        path="/gildas"
        schemas={[
          breadcrumb([{ name: 'Las Gildas', path: '/gildas' }]),
          gildasItemList(GILDAS),
        ]}
      />

      {/* HEADER */}
      <section className="sec sec-surf" data-testid="gildas-page">
        <div className="split col2">
          <div>
            <p className="eyebrow rv">Nuestra especialidad</p>
            <h1 className="D D-xl rv" style={{ marginBottom: '1.25rem' }}>Las Gildas <em>de la casa.</em></h1>
            <p className="lead rv" style={{ marginBottom: '1rem' }}>
              La gilda nació en San Sebastián en los años 40 — anchoa, oliva y guindilla en un palillo. Simple, perfecta. Lo difícil es hacerla bien, con ingredientes de verdad.
            </p>
            <p className="copy rv">
              En el Bar Cabrero las preparamos cada mañana, antes de abrir. <strong>Nunca del día anterior. Nunca de bote.</strong>
            </p>
          </div>
          <div className="rv">
            <img
              src="/dishes/gilda.jpg"
              alt="Gilda artesana del Bar Cabrero — anchoa de Santoña, oliva manzanilla y piparra verde"
              style={{ width: '100%', aspectRatio: '4/3', objectFit: 'cover', borderRadius: 'var(--r)' }}
              loading="lazy"
            />
          </div>
        </div>
      </section>

      {/* 12 VARIEDADES */}
      <section className="sec" data-testid="gildas-grid-section">
        <p className="eyebrow rv">Las 12 variedades</p>
        <h2 className="D D-md rv" style={{ marginBottom: '2.5rem' }}>Todas <em>artesanas.</em> Todas hechas a mano.</h2>
        <div className="gcards rv" data-testid="gildas-grid">
          {GILDAS.map((g, i) => (
            <article key={g.num} className="gc gc-img" data-testid={`gilda-card-${i + 1}`} style={{ animation: `fadeUp 0.5s ${i * 0.07}s both` }}>
              <div className="gc-photo">
                <img src={g.img} alt={`Gilda ${g.name} — ${g.desc}`} loading="lazy" />
              </div>
              <div className="gc-body">
                <div className="gc-n">{g.num}</div>
                <div className="gc-name">{g.name}</div>
                <p className="gc-desc">{g.desc}</p>
                <Spice level={g.spice} />
              </div>
            </article>
          ))}
          <button
            className="gc-quiz"
            onClick={() => quizRef.current?.scrollIntoView({ behavior: 'smooth' })}
            data-testid="grid-quiz-cta"
          >
            <p>"¿No sabes cuál pedir? Responde 3 preguntas y te decimos cuál es tu gilda."</p>
            <span>Hacer el quiz →</span>
          </button>
        </div>

        {/* SANTOÑA */}
        <div className="origen rv" style={{ marginTop: '3rem', borderRadius: 'var(--r)' }}>
          <div>
            <p className="eyebrow" style={{ color: 'rgba(244,247,242,0.55)' }}>Origen</p>
            <h3 className="D D-md" style={{ color: '#fff', marginBottom: '1rem' }}>
              Anchoas de <em style={{ color: 'var(--amber)' }}>Santoña DOP.</em>
            </h3>
            <p style={{ color: 'rgba(244,247,242,0.75)', lineHeight: 1.8, fontSize: '0.95rem' }}>
              A solo 60 km de Torrelavega. Bocarte del Cantábrico curado en barril durante meses, fileteado a mano. La anchoa talla 0–00 es la que marca la diferencia — sin excepciones.
            </p>
            <div className="o-tags">
              <span className="o-tag">Bocarte</span><span className="o-tag">Talla 0–00</span><span className="o-tag">DOP</span><span className="o-tag">Cantábrico</span>
            </div>
          </div>
          <div className="origen-box">
            <div className="origen-km">60<span style={{ fontSize: '1.5rem', color: 'var(--verde)' }}>km</span></div>
            <p className="origen-sub">Torrelavega → Santoña</p>
            <p style={{ color: 'rgba(244,247,242,0.65)', fontSize: '0.85rem', lineHeight: 1.7 }}>
              Cada semana, anchoas frescas. La proximidad es nuestra ventaja.
            </p>
          </div>
        </div>
      </section>

      {/* QUIZ */}
      <section id="quiz" ref={quizRef} className="sec sec-surf" data-testid="quiz-section">
        <div className="qwrap" style={{ textAlign: 'center' }}>
          <p className="eyebrow rv">Descubre tu gilda</p>
          <h2 className="D D-lg rv" style={{ marginBottom: '0.75rem' }}>¿Cuál es <em>tu Gilda?</em></h2>
          <p className="copy rv" style={{ marginBottom: '2.5rem' }}>
            Tenemos 12 variedades. Todas artesanas. Responde 3 preguntas y te decimos cuál es la tuya.
          </p>

          {step < 3 && (
            <div data-testid="quiz-card" style={{ background: 'var(--bg)', borderRadius: 'var(--r)', padding: '2.5rem 2rem', border: '1px solid var(--bord)' }}>
              <div className="qprog">
                {[0,1,2].map(i => <span key={i} className={`qdot ${i <= step ? 'on' : ''}`} />)}
              </div>
              <p className="qnum">Pregunta {step + 1} de 3</p>
              <p className="qtext" data-testid={`quiz-question-${step + 1}`}>{QUIZ_QUESTIONS[step].q}</p>
              <div className="qopts">
                {QUIZ_QUESTIONS[step].opts.map((o, idx) => (
                  <button key={o.t} className="qopt" onClick={() => answer(o.v)} data-testid={`quiz-opt-${step + 1}-${idx + 1}`}>
                    {o.t}
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="qres" data-testid="quiz-result">
              <div className="qres-photo">
                <img src={resultGilda.img} alt={`Gilda ${resultGilda.name}`} loading="lazy" />
              </div>
              <p className="qr-l">Tu gilda es</p>
              <h3 className="qr-t">{resultGilda.name}</h3>
              <p className="qr-d">{resultGilda.desc}</p>
              <div style={{ display: 'flex', gap: '0.85rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                <a className="btn btn-g" href={waLink(`Hola Bar Cabrero! Voy a probar la ${stripAccents(resultGilda.name)}. Gracias!`)} target="_blank" rel="noopener noreferrer" data-testid="quiz-result-reserve">
                  Vengo a probarla
                </a>
                <button className="btn btn-o" onClick={() => navigate('/visitanos')} data-testid="quiz-result-visit">
                  Cómo llegar
                </button>
              </div>
              <ShareGilda gilda={resultGilda.name} />
              <button className="qr-restart" onClick={reset} data-testid="quiz-restart">Repetir el quiz</button>
            </div>
          )}
        </div>
      </section>

      {/* DECONSTRUIDA — GILDA */}
      <DeconstructedDish config={GILDA_DECONSTRUCTED} />

      {/* TAKEAWAY / CATERING */}
      <TakeawayCatering variant="full" />

      {/* PEDIDO POR WHATSAPP — TEASER */}
      <section className="sec ob-teaser-section" data-testid="order-teaser">
        <div className="ob-teaser">
          <div className="ob-teaser-l">
            <p className="eyebrow rv">Pídelo por WhatsApp</p>
            <h2 className="D D-lg rv" style={{ marginBottom: '0.85rem' }}>
              Arma tu <em>bandeja</em><br />en un minuto.
            </h2>
            <p className="lead rv" style={{ marginBottom: '1.5rem' }}>
              Elige cuántas de cada variedad, día y hora dentro del horario de apertura. Mensaje pre-rellenado, tú lo envías por WhatsApp.
            </p>
            <Link to="/pedido" className="btn btn-g rv" data-testid="order-teaser-cta">
              Configurar mi bandeja →
            </Link>
          </div>
          <div className="ob-teaser-r rv" aria-hidden="true">
            <div className="ob-teaser-pill">12<small>variedades</small></div>
            <div className="ob-teaser-pill">2 días<small>antelación</small></div>
            <div className="ob-teaser-pill">A mano<small>cada gilda</small></div>
            <div className="ob-teaser-pill">∞<small>personas</small></div>
          </div>
        </div>
      </section>

      {/* CANAPÉS */}
      <CanapesSection />

      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(14px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @media (prefers-reduced-motion: reduce) {
          .gc { animation: none !important; }
        }
      `}</style>
    </>
  );
}
